// Copyright 2025, University of Maryland, All Rights Reserved
import React from "react"
import { BaseField, type BaseFieldEditorProps } from "./BaseField.tsx"
import { IntegerFieldEditor } from "./IntegerField.tsx"
import { EnumFieldEditor } from "./EnumField.tsx"
import type { SchemaField } from "@/types/schema-fields"

export const ScoreFieldEditor = React.memo(({
  field,
  onFieldUpdate,
  ...restProps
}: BaseFieldEditorProps) => {
  const config = field.config

  const update_config = (config_updates: Record<string, any>) => {
    onFieldUpdate(field, {
      config: { ...config, ...config_updates }
    })
  }

  // Create virtual integer field for score range
  const score_range_field: SchemaField = {
    ...field,
    id: 'score',
    title: undefined, // Hide title for embedded component
    config: {
      ...config,
      min: config.score_min ?? 1,
      max: config.score_max ?? 10,
      title: 'Score Range'
    }
  }

  // Create virtual enum field for score types
  const score_types_field: SchemaField = {
    ...field,
    id: 'score_type',
    title: undefined, // Hide title for embedded component
    config: {
      ...config,
      enum_options: config.type_options || ['positive', 'negative', 'neutral'],
      title: 'Score Types'
    }
  }

  const handle_range_update = (_: SchemaField, updates: Partial<SchemaField>) => {
    if (updates.config) {
      update_config({
        score_min: updates.config.min,
        score_max: updates.config.max
      })
    }
  }

  const handle_types_update = (_: SchemaField, updates: Partial<SchemaField>) => {
    if (updates.config) {
      update_config({
        type_options: updates.config.enum_options
      })
    }
  }

  return (
    <BaseField field={field} onFieldUpdate={onFieldUpdate} {...restProps}>
      <div className="space-y-4">
        {/* Score Range using IntegerFieldEditor */}
        <div className="bg-gray-25 p-3 rounded border">
          <IntegerFieldEditor
            field={score_range_field}
            onFieldUpdate={handle_range_update}
            onRemove={() => {}} // No-op for embedded component
            isLoading={restProps.isLoading}
            readonly_id={true}
            non_deletable={true}
            hide_title={true}
          />
        </div>

        {/* Score Types using EnumFieldEditor */}
        <div className="bg-gray-25 p-3 rounded border">
          <EnumFieldEditor
            field={score_types_field}
            onFieldUpdate={handle_types_update}
            onRemove={() => {}} // No-op for embedded component
            isLoading={restProps.isLoading}
            readonly_id={true}
            non_deletable={true}
            hide_title={true}
          />
        </div>
        
        <p className="text-xs text-muted-foreground">
          Configure the numeric range and classification types for this score field
        </p>
      </div>
    </BaseField>
  )
})

ScoreFieldEditor.displayName = 'ScoreFieldEditor'
